import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonList,
  IonRow,
  IonText,
  IonTitle,
  IonToggle,
  IonToolbar,
  LoadingController,
  ModalController,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IonicScrollManagerService, markFormAsInvalid } from '@okcargo-apps/layout-ionic';
import { MessagesUiService } from '@okcargo-apps/messages-ui';
import { EUserRole, IUser } from '@okcargo-apps/shared-domain';
import { detailedEmailValidator, phoneUEValidator } from '@okcargo-apps/utils';
import { AccountService } from '../../account';
import { mapFormValueToUser, mapUserToFormValue } from '../user-form.mappers';
import { IUserFormValue, USERS_LIST_MESSAGE_OBJECT_ID, USER_ROLE_OPTIONS } from '../users-store.model';
import { UsersMutationsService } from '../users-mutations.service';
import { UsersStoreService } from '../users-store.service';

@Component({
  selector: 'lib-user-edit-modal',
  templateUrl: './user-edit-modal.component.html',
  styleUrls: ['./user-edit-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    IonContent,
    IonTitle,
    IonIcon,
    IonButtons,
    IonHeader,
    IonButton,
    IonItem,
    IonList,
    IonInput,
    IonToggle,
    IonCheckbox,
    IonText,
    IonGrid,
    IonRow,
    IonCol,
    IonFooter,
    IonToolbar,
    TranslateModule,
  ],
})
export class UserEditModalComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);
  private readonly modalCtrl = inject(ModalController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly translate = inject(TranslateService);
  private readonly accountService = inject(AccountService);
  private readonly messagesUiService = inject(MessagesUiService);
  private readonly ionicScrollManagerService = inject(IonicScrollManagerService);
  private readonly usersStoreService = inject(UsersStoreService);
  private readonly usersMutationsService = inject(UsersMutationsService);

  // Comes from the component that opens the modal (ModalController componentProps).
  public user: IUser | null = null;
  /** Roles preselected when creating (e.g. DRIVER when creating a driver from the assignation modal). */
  public presetRoles: EUserRole[] = [];

  /** True when editing the active app user (its own roles/status cannot be changed). */
  public readonly isCurrentUser = computed<boolean>(() => {
    const account = this.accountService.account();
    const user = this.user;
    return !!user?.id && (user.id === account.contactId || user.id === account.id);
  });

  protected readonly showRolesError = signal(false);

  /** Every role is shown; the ones the active user cannot manage are rendered disabled. */
  protected readonly roleOptions = USER_ROLE_OPTIONS;

  /** Roles the active user is not authorized to manage (rendered disabled and warned about). */
  protected readonly unmanageableRoleOptions = computed(() =>
    USER_ROLE_OPTIONS.filter((option) => !this.usersStoreService.manageableRoles().includes(option.role)),
  );

  protected readonly unmanageableRolesLabel = computed(() =>
    this.unmanageableRoleOptions()
      .map((option) => this.translate.instant(option.labelKey))
      .join(', '),
  );

  /**
   * True when the edited user holds roles the active user cannot manage: those permissions cannot
   * be changed, and the user cannot be disabled (nor removed).
   */
  public readonly hasUnmanageableTargetRoles = computed(() =>
    (this.user?.roles ?? []).some((role) => !this.usersStoreService.manageableRoles().includes(role)),
  );

  protected userForm: FormGroup = this.fb.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    email: ['', [Validators.required, detailedEmailValidator()]],
    phone: ['', [Validators.required, phoneUEValidator()]],
    enabled: [true],
    roles: this.fb.control<EUserRole[]>([]),
  });

  public get isEdit(): boolean {
    return this.user !== null;
  }

  public ngOnInit(): void {
    if (!this.user) {
      if (this.presetRoles?.length) {
        const manageableRoles = this.usersStoreService.manageableRoles();
        this.rolesControl.setValue(this.presetRoles.filter((role) => manageableRoles.includes(role)));
      }
      return;
    }

    this.userForm.patchValue(mapUserToFormValue(this.user));
    // Email and phone cannot be modified once the user has been created.
    this.userForm.controls['email'].disable();
    this.userForm.controls['phone'].disable();

    // The active user cannot change its own enabled status, and neither the status of a user that
    // holds permissions it is not authorized to manage.
    if (this.isCurrentUser() || this.hasUnmanageableTargetRoles()) {
      this.userForm.controls['enabled'].disable();
    }
  }

  protected isRoleSelected(role: EUserRole): boolean {
    return (this.rolesControl.value ?? []).includes(role);
  }

  protected isRoleManageable(role: EUserRole): boolean {
    return this.usersStoreService.manageableRoles().includes(role);
  }

  protected toggleRole(role: EUserRole, checked: boolean): void {
    if (this.isCurrentUser() || !this.isRoleManageable(role)) return;

    const current = this.rolesControl.value ?? [];
    const next = checked ? [...current, role] : current.filter((item) => item !== role);
    this.rolesControl.setValue(next);
    this.showRolesError.set(false);
  }

  protected cancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  // TODO extract this helper (duplicated in vehicle-edit-modal and the registration forms) to a shared utility.
  protected getErrorMessage(controlName: string): string | null {
    const control = this.userForm.controls[controlName];
    const errors = control?.errors;
    if (!errors) return null;

    if (typeof errors['message'] === 'string') return errors['message'];

    const firstErrorKey = Object.keys(errors)[0];
    const errorData = errors[firstErrorKey];

    if (typeof errorData === 'string') return errorData;
    return errorData?.message ?? 'error.mandatory';
  }

  protected async confirm(): Promise<void> {
    // The active user may keep the target's existing non-manageable roles, but must not add new
    // ones. At least one role is required.
    const selectedRoles = this.rolesControl.value ?? [];
    const originalRoles = this.user?.roles ?? [];
    const addedUnmanageableRole = selectedRoles.some(
      (role) => !this.isRoleManageable(role) && !originalRoles.includes(role),
    );
    const rolesInvalid = !selectedRoles.length || addedUnmanageableRole;

    if (rolesInvalid) {
      this.showRolesError.set(true);
    }

    if (this.userForm.invalid || rolesInvalid) {
      markFormAsInvalid(this.userForm, this.elementRef.nativeElement, this.injector);
      return;
    }

    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('general.send_data'),
    });
    await loading.present();

    try {
      const user = mapFormValueToUser(
        this.userForm.getRawValue() as IUserFormValue,
        this.user?.id ?? crypto.randomUUID(),
        this.user,
      );
      const wasEdit = this.isEdit;
      const name = `${user.firstName} ${user.lastName}`.trim();

      if (wasEdit) {
        await this.usersMutationsService.updateUser(user);
      } else {
        await this.usersMutationsService.createUser(user);
      }

      this.messagesUiService.setMessage({
        objectId: USERS_LIST_MESSAGE_OBJECT_ID,
        message: [this.translate.instant(wasEdit ? 'users.updated_message' : 'users.created_message', { name })],
      });

      loading.message = this.translate.instant('general.loading_data');
      await this.usersStoreService.reloadAfterSaveData();
      await this.modalCtrl.dismiss(user, 'confirm');
      setTimeout(() => this.ionicScrollManagerService.scrollToTop(), 300);
    } catch (error) {
      console.warn(error);
    } finally {
      await loading.dismiss();
    }
  }

  private get rolesControl(): FormControl<EUserRole[]> {
    return this.userForm.controls['roles'] as FormControl<EUserRole[]>;
  }
}
